import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

const Navbar = ({ user }) => {
    const { instance, accounts } = useMsal();
    const account = accounts[0];
    const hasAuthenticatedSession = Boolean(account || user);
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    const handleLogout = async () => {
        try {
            await instance.logoutRedirect({
                account: account,
            });
        } catch (error) {
            console.error("Logout error:", error);
        }
    };
    
    const isActive = (path) => location.pathname === path;
    const isAdminPath = location.pathname.startsWith("/admin");
    const returnToParam = location.pathname.startsWith("/admin")
        ? "admin"
        : location.pathname.startsWith("/my-events")
            ? "my-events"
            : "my-events";

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname, location.search]);
    
    return (
        <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <div className="flex items-center gap-8">
                        <Link to="/" className="flex items-center gap-3">
                            <img 
                                src="https://ku-bu.de/wp-content/uploads/2022/06/KuBu_neu_1200.png" 
                                alt="Ku-Bu" 
                                className="h-10"
                            />
                            <span className="font-bold text-gray-900 text-lg hidden sm:inline">
                                Veranstaltungen
                            </span>
                        </Link>
                        
                        <div className="hidden md:flex flex-wrap items-center gap-2">
                            <Link
                                to="/"
                                data-testid="nav-events"
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                                    isActive("/")
                                        ? "bg-purple-100 text-purple-700"
                                        : "text-gray-600 hover:bg-gray-100"
                                }`}
                            >
                                Kalender
                            </Link>
                            {hasAuthenticatedSession ? (
                                <>
                                    <Link
                                        to={`/create?returnTo=${returnToParam}`}
                                        data-testid="nav-create"
                                        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-emerald-600 hover:to-teal-600"
                                    >
                                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-lg leading-none">+</span>
                                        Neue Veranstaltung
                                    </Link>
                                    <Link
                                        to="/my-events"
                                        data-testid="nav-my-events"
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                                            isActive("/my-events")
                                                ? "bg-purple-100 text-purple-700"
                                                : "text-gray-600 hover:bg-gray-100"
                                        }`}
                                    >
                                        Meine Meldungen
                                    </Link>
                                </>
                            ) : (
                                <Link
                                    to="/login"
                                    className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                                >
                                    Anmelden
                                </Link>
                            )}
                            {hasAuthenticatedSession && user?.role === "admin" && (
                                <Link 
                                    to="/admin" 
                                    data-testid="nav-admin"
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                                        isAdminPath
                                            ? "bg-purple-100 text-purple-700" 
                                            : "text-gray-600 hover:bg-gray-100"
                                    }`}
                                >
                                    Admin Panel
                                </Link>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2 sm:gap-4">
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen((prev) => !prev)}
                            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-100"
                            aria-expanded={mobileMenuOpen}
                            aria-label={mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
                        >
                            {mobileMenuOpen ? (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                        {hasAuthenticatedSession ? (
                            <>
                                <div className="hidden sm:block text-right">
                                    <p className="text-sm font-medium text-gray-900" data-testid="user-name">
                                        {user?.name || account?.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {user?.role === "admin" ? "Administrator" : "Veranstalter"}
                                    </p>
                                </div>
                                <button
                                    data-testid="logout-button"
                                    onClick={handleLogout}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    <span className="hidden sm:inline">Abmelden</span>
                                </button>
                            </>
                        ) : (
                            <Link
                                to="/login"
                                className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                            >
                                Anmelden
                            </Link>
                        )}
                    </div>
                </div>
                {mobileMenuOpen && (
                    <div className="border-t border-gray-200 py-3 md:hidden">
                        <div className="space-y-2">
                            <Link
                                to="/"
                                data-testid="nav-events-mobile"
                                className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                    isActive("/")
                                        ? "bg-purple-100 text-purple-700"
                                        : "text-gray-700 hover:bg-gray-100"
                                }`}
                            >
                                Kalender
                            </Link>
                            {hasAuthenticatedSession ? (
                                <>
                                    <Link
                                        to={`/create?returnTo=${returnToParam}`}
                                        data-testid="nav-create-mobile"
                                        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-2 text-sm font-semibold text-white"
                                    >
                                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-lg leading-none">+</span>
                                        Neue Veranstaltung
                                    </Link>
                                    <Link
                                        to="/my-events"
                                        data-testid="nav-my-events-mobile"
                                        className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                            isActive("/my-events")
                                                ? "bg-purple-100 text-purple-700"
                                                : "text-gray-700 hover:bg-gray-100"
                                        }`}
                                    >
                                        Meine Meldungen
                                    </Link>
                                </>
                            ) : (
                                <Link
                                    to="/login"
                                    className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                                >
                                    Anmelden
                                </Link>
                            )}
                            {hasAuthenticatedSession && user?.role === "admin" && (
                                <Link
                                    to="/admin"
                                    data-testid="nav-admin-mobile"
                                    className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                        isAdminPath
                                            ? "bg-purple-100 text-purple-700"
                                            : "text-gray-700 hover:bg-gray-100"
                                    }`}
                                >
                                    Admin Panel
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
